import numeral from 'numeral';
import FpField from '@/components/fpComponents/FpField/FpField.vue';
import {isValidIBAN} from 'ibantools';
import moment from 'moment';
import {isObject, isEmpty, flow, every, pick, pickBy } from 'lodash-es';
import {isPersonalIdNumberValid} from '@/components/fpComponents/services/countrySpecific/personalIdNumber';
import EnvironmentService from '@/env/EnvironmentService';
import {unmaskText} from '@/components/fpComponents/services/unmask';
import {SizeProps} from '@/components/fpComponents/FpField/colsSizeProps';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {i18nFormatter} from '@/services/StringFormatter';

const DATE_FORMAT = 'DDMMYYYY';
const FpValidationMixin = {
  components: { FpField, },
  props: {
    // eslint-disable-next-line no-return-assign,no-sequences
    ...(SizeProps.reduce((a, b) => (a[b] = {}, a), {})),
    /**
     * @model
     */
    value: {
      default: null,
    },
    id: {
      default: null,
    },
    customRules: {
      default: () => [],
      type: Array,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    password: {
      type: Boolean,
      default: false,
    },
    label: {
      required: false,
      type: String,
    },
    description: {
      type: String,
    },
    tooltip: {
      type: String,
    },
    required: {
      default: false,
      type: Boolean,
    },
    maxValue: {
      default: false,
    },
    minValue: {
      default: false,
    },
    minDate: {
      default: false,
    },
    maxDate: {
      default: false,
    },
    nameValidation: {
      default: false,
    },
  },
  methods: {
    isNotExist: (v) => typeof v === 'undefined' ||
      v === null ||
      v === '' ||
      (typeof v === 'string' && v.trim() === '') ||
      (isObject(v) && isEmpty(v)),
    getRegon9ControlSum(regon) {
      const weightArray = [8, 9, 2, 3, 4, 5, 6, 7,];
      const controlDigit = regon
        .slice(0, 8)
        .split('')
        .map((v, index) => Number(v) * weightArray[index])
        .reduce((a, b) => a + b);
      const lastDigit = controlDigit % 11 > 9 ? 0 : controlDigit % 11;
      return regon.slice(-1) === String(lastDigit);
    },
    getRegon14ControlSum(regon) {
      const weightArray = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8,];
      const controlDigit = regon
        .slice(0, 13)
        .split('')
        .map((v, index) => Number(v) * weightArray[index])
        .reduce((a, b) => a + b);
      const lastDigit = controlDigit % 11 > 9 ? 0 : controlDigit % 11;
      return regon.slice(-1) === String(lastDigit);
    },
    validateDate(date) {
      return moment(date, DATE_FORMAT, true).isValid();
    },
    unMaskValue(v) {
      if (this.pickedMask) {
        const unmaskedText = unmaskText(v);
        if (unmaskedText) {
          return unmaskedText.substr(0, this.pickedMaskLength);
        } else {
          return v;
        }
      } else {
        return v;
      }
    },
    validateName(v) {
      const regex = /^(?!.*[ \-']{2,})(?=\p{L}.*\p{L}$)(?=.{3,})[\p{L} \-']*$/u;
      return regex.test(v);
    },
    validateUrl(v) {
      return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(v);
    }
  },
  computed: {
    valid() {
      return flow(
        array => array.map(rule => rule(this.value)),
        mappedArray => every(mappedArray, ruleResult => ruleResult === true)
      )(this.rules);
    },
    rules() {
      const keys = flow([
        array => pickBy(array,(value) => value !== undefined && value !== null && value !== false),
        pickedArray => Object.keys(pickedArray),
      ])(this.$props);
      const alias = (keys, key, alias) => {
        const keyIndex = keys.indexOf(key);
        if (keyIndex !== -1) {
          keys.splice(keyIndex, 1, alias);
        }
      };
      alias(keys, 'pesel', 'personalId');
      alias(keys, 'nip', 'taxId');
      const rules = flow(
        array => pick(array, ...keys),
        pickedArray => Object.values(pickedArray),
        valuesOfPickedArray => valuesOfPickedArray
          .map(rule => this.isNumber ? (v) => rule(numeral(v)._value) : (v) => rule(this.unMaskValue(v)))
      )(this.validationRules);
      if (this.customRules) {
        this.customRules.forEach((rule) => rules.push(rule));
      }
      return rules;
    },
  },
  data() {
    const domain = EnvironmentService.Environment.appDomain;
    const isPersonalIdValidatorObject = isPersonalIdNumberValid[domain];
    const appDomainConfig = EnvironmentService.Environment.getAppDomainConfig();
    const taxIdValidatorObject = appDomainConfig.taxIdentificationNumber.validator;
    return {
      validationRules: {
        required: (v) => !this.isNotExist(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_REQUIRED,
        password: (v) => this.isNotExist(v) || /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/.test(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_PASSWORD_INVALID,
        maxValue: (v) => this.isNotExist(v) || v <= this.maxValue || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MAX_VALUE, {value: (this.maxValue),}
        ),
        minValue: (v) => this.isNotExist(v) || v >= this.minValue || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MIN_VALUE, {value: this.minValue,}
        ),
        minDate: (v) => this.isNotExist(v) || moment(v).isSameOrAfter(this.minDate, 'day') || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MIN_DATE, {value: this.minDate,}
        ),
        maxDate: (v) => this.isNotExist(v) || moment(this.maxDate).isSameOrAfter(v, 'day') || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MAX_DATE, {value: this.maxDate,}
        ),
        date: (v) => this.isNotExist(v) || this.validateDate(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_DATE,
        minLength: (v) => this.isNotExist(v) ||
          v.length >= this.minLength || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MIN_LENGTH, {value: this.minLength,}
        ),
        maxLength: (v) => this.isNotExist(v) ||
          v.length <= this.maxLength || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_MAX_LENGTH,{value: this.maxLength,}
        ),
        length: (v) => this.isNotExist(v) || v.length === this.length || i18nFormatter(
          I18NGetter().VALIDATION.NEW_TRANSLATION_LENGTH, {value: this.length,}
        ),
        iban: (v) => this.isNotExist(v) || isValidIBAN(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_IBAN,
        bban: (v) => this.isNotExist(v) || isValidIBAN(`PL${v}`) || I18NGetter().VALIDATION.NEW_TRANSLATION_BBAN,
        regon: (v) => this.isNotExist(v) ||
          ((v.length === 9 && this.getRegon9ControlSum(v)) ||
            (v.length === 14 && this.getRegon14ControlSum(v))) || I18NGetter().VALIDATION.NEW_TRANSLATION_REGON,
        taxId: (v) => this.isNotExist(v) ||
        !taxIdValidatorObject ||
          ((taxIdValidatorObject.length ? v.length === taxIdValidatorObject.length : true) &&
            taxIdValidatorObject.validationFunc(v)) ||
          I18NGetter().VALIDATION.NEW_TRANSLATION_TAX_ID,
        // eslint-disable-next-line no-useless-escape
        email: (v) => this.isNotExist(v) || /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || `${this.customErrorMessage || I18NGetter().VALIDATION.NEW_TRANSLATION_EMAIL}`,
        personalId: (v) => this.isNotExist(v) || (isPersonalIdValidatorObject.validationFunc(v) && isPersonalIdValidatorObject.length === v.length) || `${this.customErrorMessage ? this.customErrorMessage : i18nFormatter(I18NGetter().VALIDATION.NEW_TRANSLATION_PERSONAL_ID, {personalIdNumberType: appDomainConfig.personalIdNumber.type,})}`,
        url: (v) => this.isNotExist(v) || this.validateUrl(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_URL,
        text: (v) => this.isNotExist(v) || /^[\s\p{L}]+$/u.test(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_TEXT,
        // eslint-disable-next-line vue/max-len
        nameValidation: (v) => this.isNotExist(v) || this.validateName(v) || I18NGetter().VALIDATION.NEW_TRANSLATION_TEXT,
        branchBusinessCard: (v) => this.isNotExist(v) || (this.validateUrl(v) && /(https)?(:\/\/)?(www.)?business\.google\.com\/n\/.+/.test(v)) || I18NGetter().VALIDATION.NEW_TRANSLATION_TEXT,
      },
    };
  },
};

export default FpValidationMixin;
